<template>
  <div id="app">
    <hello></hello>
    <onboarding-modal></onboarding-modal>
  </div>
</template>

<script>
import Hello from './components/Hello'
import OnboardingModal from './components/OnboardingModal'

export default {
  name: 'app',
  components: {
    Hello,
    OnboardingModal
  }
}
</script>

<style>
/* ============================================================
   Códice Nepohualtzintzin — Global Foundation
   Agent A: design tokens, page (amate), base typography
   ============================================================ */

:root {
  /* — Paper / surfaces — */
  --amate:           #f3e7cc;
  --amate-warm:      #ede0c0;
  --amate-shadow:    #d9c8a3;

  /* — Ink — */
  --obsidiana:       #1a1814;
  --obsidiana-soft:  #3a3530;

  /* — Sacred accents — */
  --cinabrio:        #b73225;
  --cinabrio-deep:   #8a2018;
  --jade:            #2d6e5f;
  --jade-deep:       #1e4d42;
  --maiz:            #d9a441;
  --maiz-deep:       #b8861f;
  /* Accented aliases (some components reference --maíz directly) */
  --maíz:            #d9a441;
  --maíz-deep:       #b8861f;
  --cielo:           #5e7a8a;

  /* — Traces / grain — */
  --ink-trace:       rgba(26, 24, 20, 0.12);
  --ink-trace-soft:  rgba(26, 24, 20, 0.06);
  --ink-trace-strong:rgba(26, 24, 20, 0.22);
  --page-grain:      rgba(58, 53, 48, 0.04);

  /* — Aliases for component agents (semantic) — */
  --bg-page:         var(--amate);
  --bg-elevated:     var(--amate-warm);
  --text-primary:    var(--obsidiana);
  --text-secondary:  var(--obsidiana-soft);
  --accent-primary:  var(--cinabrio);
  --accent-secondary:var(--jade);
  --accent-gold:     var(--maiz);
  --rule:            var(--ink-trace);

  /* — Typography stacks — */
  --font-display:    'Cinzel Decorative', 'Cinzel', 'Trajan Pro', Georgia, serif;
  --font-subhead:    'Cinzel', 'Trajan Pro', Georgia, serif;
  --font-body:       'Crimson Pro', 'Iowan Old Style', Georgia, 'Times New Roman', serif;
  --font-mono:       'DM Mono', 'Iosevka', 'JetBrains Mono', ui-monospace, 'Menlo', monospace;

  /* — Rhythm — */
  --measure:         1100px;
  --gutter:          clamp(1rem, 3vw, 2.25rem);
  --rhythm:          1.55;

  /* — Motion — */
  --ease-codex:      cubic-bezier(0.22, 0.61, 0.36, 1);
  --dur-quick:       180ms;
  --dur-base:        320ms;
  --dur-slow:        620ms;
  --stagger:         70ms;
}

/* — Reset (soft) — */
*,
*::before,
*::after {
  box-sizing: border-box;
}

html,
body {
  margin: 0;
  padding: 0;
  min-height: 100%;
}

html {
  background: var(--amate);
  /* Honor user motion preferences for subsequent animations */
  scroll-behavior: smooth;
}

body {
  color: var(--obsidiana);
  font-family: var(--font-body);
  font-size: 18px;
  line-height: var(--rhythm);
  font-weight: 400;
  font-feature-settings: "kern" 1, "liga" 1, "onum" 1;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;

  /* Amate paper: warm base wash + radial vignette + SVG fibrous noise.
     Layered so each component reads as a single page of bark paper. */
  background-color: var(--amate);
  background-image:
    /* subtle vignette pulling toward warm shadow at edges */
    radial-gradient(
      ellipse at 50% 0%,
      rgba(255, 248, 228, 0.55) 0%,
      rgba(243, 231, 204, 0) 55%
    ),
    radial-gradient(
      ellipse at 50% 100%,
      rgba(217, 200, 163, 0.35) 0%,
      rgba(243, 231, 204, 0) 60%
    ),
    /* fibrous bark-paper noise via inline SVG turbulence */
    url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='360' height='360' viewBox='0 0 360 360'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch' seed='7'/><feColorMatrix values='0 0 0 0 0.227  0 0 0 0 0.208  0 0 0 0 0.188  0 0 0 0.18 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.55'/></svg>"),
    /* longer fibers, very faint */
    url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='720' height='720' viewBox='0 0 720 720'><filter id='f'><feTurbulence type='turbulence' baseFrequency='0.012 0.9' numOctaves='2' seed='3'/><feColorMatrix values='0 0 0 0 0.18  0 0 0 0 0.16  0 0 0 0 0.14  0 0 0 0.10 0'/></filter><rect width='100%25' height='100%25' filter='url(%23f)'/></svg>");
  background-repeat: no-repeat, no-repeat, repeat, repeat;
  background-size: 100% 60vh, 100% 50vh, 360px 360px, 720px 720px;
  background-attachment: fixed, fixed, fixed, fixed;
}

/* Page-edge ink trace — a discreet sense of the paper having a border. */
body::before {
  content: "";
  position: fixed;
  inset: 0;
  pointer-events: none;
  background:
    linear-gradient(to right, var(--ink-trace-soft), transparent 4%, transparent 96%, var(--ink-trace-soft)),
    linear-gradient(to bottom, var(--ink-trace-soft), transparent 3%, transparent 97%, var(--ink-trace-soft));
  z-index: 0;
}

/* — App container — */
#app {
  position: relative;
  z-index: 1;
  max-width: var(--measure);
  margin: 0 auto;
  padding: clamp(2.5rem, 6vw, 4.5rem) var(--gutter) 4rem;
  color: var(--obsidiana);
  text-align: center;
  font-family: var(--font-body);
}

/* — Headings default to display stack; components can override — */
h1, h2, h3, h4, h5, h6 {
  font-family: var(--font-subhead);
  color: var(--obsidiana);
  font-weight: 600;
  letter-spacing: 0.04em;
  line-height: 1.2;
  margin: 0 0 0.6em;
  font-feature-settings: "kern" 1, "liga" 1, "smcp" 0;
}

h1 {
  font-family: var(--font-display);
  font-weight: 700;
  letter-spacing: 0.08em;
  font-feature-settings: "kern" 1, "liga" 1, "dlig" 1;
}

/* — Body links — */
a {
  color: var(--cinabrio-deep);
  text-decoration: none;
  border-bottom: 1px solid var(--ink-trace);
  transition: color var(--dur-quick) var(--ease-codex),
              border-color var(--dur-quick) var(--ease-codex);
}
a:hover,
a:focus-visible {
  color: var(--cinabrio);
  border-bottom-color: var(--cinabrio);
}

/* — Mono for vigesimal / decimal numerals — */
code, kbd, samp, pre,
.mono, .numeral {
  font-family: var(--font-mono);
  font-feature-settings: "tnum" 1, "zero" 1;
}

/* — Selection — */
::selection {
  background: var(--maiz);
  color: var(--obsidiana);
}

/* — Focus ring (accessibility) — */
:focus-visible {
  outline: 2px solid var(--cinabrio);
  outline-offset: 3px;
  border-radius: 2px;
}

/* — Glyphic divider utility for component agents (•—•—•) — */
.codex-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  color: var(--ink-trace-strong);
  font-family: var(--font-mono);
  letter-spacing: 0.4em;
  margin: 2rem auto;
  font-size: 0.8rem;
}
.codex-divider::before,
.codex-divider::after {
  content: "";
  flex: 0 1 6rem;
  height: 1px;
  background: linear-gradient(to right, transparent, var(--ink-trace), transparent);
}

/* — Stagger fade-in on load (ceremonial, reverent) — */
@keyframes codex-rise {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

#app > * {
  animation: codex-rise var(--dur-slow) var(--ease-codex) both;
}
#app > *:nth-child(1) { animation-delay: 0ms; }
#app > *:nth-child(2) { animation-delay: var(--stagger); }
#app > *:nth-child(3) { animation-delay: calc(var(--stagger) * 2); }
#app > *:nth-child(4) { animation-delay: calc(var(--stagger) * 3); }
#app > *:nth-child(5) { animation-delay: calc(var(--stagger) * 4); }

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.001ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.001ms !important;
    scroll-behavior: auto !important;
  }
}
</style>



// WEBPACK FOOTER //
// App.vue?791c5234